import { ChoiceItem } from '@ingka/choice';
import ThemeFontStyleTypeEnum from '@inter-ikea-kompis/enums/lib/ThemeFontStyleTypeEnum';
import { PlatsaSoftClosingHingeComponent } from '@inter-ikea-storageone/range-platsa';
import { useCore } from 'contexts/core';
import InfoIcon from '@ingka/ssr-icon/paths/information-circle';
import { getProductEntities } from 'ducks/core';
import { useAppDispatch } from 'ducks/hooks';
import { getMarketProducts } from 'ducks/products';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import Button, { ButtonTypeEnum } from 'ui/Button/Button';
import SkapaChoice from 'ui/Choice/Choice';
import Divider from 'ui/Divider/Divider';
import Title, { HeadingType } from 'ui/Typography/Title';
import { isMobile } from 'utils/platform';
import { showSoftClosingHingesSheet } from 'rangeConfig/platsa/features/SoftClosingHinges/SoftClosingHingesFeature';

const Wrapper = styled.div`
  margin-top: ${({ theme }) => (isMobile() ? 0 : theme.space.xl)};
  margin-bottom: ${({ theme }) => (isMobile() ? 0 : theme.space.l)};
  padding: 0 ${({ theme }) => (isMobile() ? theme.space.l : 0)};
`;

const StyledSkapaChoice = styled(SkapaChoice)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 20px;
  margin-top: 10px;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

type ChoiceOption = {
  id: string;
  title: string;
  onSelect: () => void;
  caption?: string;
};

const ChoiceChildren = (options: ChoiceOption[]) => {
  const { formatMessage } = useIntl();
  return options.map(option => (
    <ChoiceItem
      id={option.id}
      title={formatMessage({ id: option.title })}
      onClick={() => option.onSelect()}
      key={option.id}
    />
  ));
};

export default function PlatsaHingeTypeSelector() {
  const coreApi = useCore();
  const dispatch = useAppDispatch();
  const scene = coreApi.getServices().sceneManager.scene;
  const { formatMessage } = useIntl();
  const [idx, setIdx] = useState(scene.hasComponent(PlatsaSoftClosingHingeComponent.id) ? 1 : 0);
  const allProducts = useSelector(getMarketProducts);
  const productsInScene = useSelector(getProductEntities);

  const handleStandardHingeSelect = () => {
    coreApi.history.historyAction(() => {
      scene?.removeComponent(PlatsaSoftClosingHingeComponent.id);
    });
  };

  const handleSoftClosingHingeSelect = () => {
    coreApi.history.historyAction(() => {
      scene?.addComponent(new PlatsaSoftClosingHingeComponent());
    });
  };

  const hingeChoiceOptions = [
    {
      id: 'standard-hinge',
      title: 'storageone.standardHingeChoiceTitle',
      onSelect: handleStandardHingeSelect
    },
    {
      id: 'soft-closing-hinge',
      title: 'storageone.softClosingHingeChoiceTitle',
      onSelect: handleSoftClosingHingeSelect
    }
  ];

  useEffect(() => {
    if (scene?.hasComponent(PlatsaSoftClosingHingeComponent.id)) {
      setIdx(1);
    } else {
      setIdx(0);
    }
  }, [idx, setIdx, allProducts, scene, productsInScene]);

  return (
    <Wrapper>
      {!isMobile() && (
        <TitleContainer>
          <Title type={ThemeFontStyleTypeEnum.headingXSmall} heading={HeadingType.HeadingThree}>
            {formatMessage({ id: 'storageone.platsaChooseHingeWidgetTitle' })}
          </Title>
          <Button
            ssrIcon={InfoIcon}
            aria-label={formatMessage({ id: 'storageone.softClosingHingeChoiceTitle' })}
            onClick={e => {
              dispatch(showSoftClosingHingesSheet());
              e.stopPropagation();
            }}
            size={'small'}
            iconOnly
            type={ButtonTypeEnum.tertiary}
          />
        </TitleContainer>
      )}
      <StyledSkapaChoice children={ChoiceChildren(hingeChoiceOptions)} preselectedIndex={idx} />
      {!isMobile() && <Divider />}
    </Wrapper>
  );
}
